import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, Drawer, IconButton, Stack, Tooltip } from "@mui/material";
import { captureException } from "@sentry/react";
import IdBadge from "components/IdBadge";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useLidarClearance } from "./LidarClearanceContext";
import StopValueFields from "./StopValueFields";
import { replaceColorScaleStops } from "./api";
import type { LidarClearanceColorScale } from "./schemas";
import t from "./translations";
import { newStopRow, useStopRows } from "./useStopRows";

type Props = {
  scale: LidarClearanceColorScale | null;
  onClose: () => void;
};

export default function ColorScaleDetailsDrawer({ scale, onClose }: Props) {
  const { refetch } = useLidarClearance();
  const { stops, setStops, addStop, updateStop, removeStop } = useStopRows();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStops(scale ? scale.colors.map(newStopRow) : []);
  }, [scale, setStops]);

  if (!scale) return null;

  const original = scale.colors;
  const dirty =
    stops.length !== original.length ||
    stops.some((stop, index) => stop.color !== original[index]);
  const canSave = dirty && !saving;

  const save = async () => {
    setSaving(true);
    try {
      await replaceColorScaleStops(
        scale.region,
        scale.color_scale_id,
        stops.map((stop) => stop.color)
      );
      await refetch();
    } catch (error) {
      captureException(error, {
        event_id: "lidarClearance.updateColorScale",
      });
      toast.error(t.FailedToUpdateColorScale);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={scale !== null} onClose={onClose}>
      <Box sx={{ width: 420, p: 3 }} data-testid="color-scale-details-drawer">
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <IdBadge
              prefix={scale.region.toUpperCase()}
              id={scale.color_scale_id}
            />
          </Box>
          <Tooltip title={t.SaveButton}>
            <span>
              <IconButton
                color="primary"
                disabled={!canSave}
                onClick={save}
                data-testid="color-scale-drawer.save"
              >
                <SaveIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <Stack>
          {stops.map((stop, index) => (
            <Stack
              key={stop.key}
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                px: 1,
                py: 1,
                backgroundColor:
                  index % 2 === 0 ? "background.paper" : "grey.100",
              }}
            >
              <StopValueFields
                position={index + 1}
                color={stop.color}
                onColorChange={(color) => updateStop(stop.key, color)}
                disabled={saving}
              />
              <IconButton
                size="small"
                aria-label={t.RemoveStop}
                onClick={() => removeStop(stop.key)}
                disabled={saving}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>

        <Button
          startIcon={<AddIcon />}
          onClick={addStop}
          disabled={saving}
          sx={{ alignSelf: "flex-start", mt: 2 }}
        >
          {t.AddStopButton}
        </Button>
      </Box>
    </Drawer>
  );
}
